import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";

import { APP_CONFIG } from "../../config/config";
import { AppConfig } from "../../config/config.type";
import { AbstractRepository } from "../../shared/repositories/abstract.repository";
import { FilterInterface, FindResult } from "../../shared/repositories/pagination.interface";
import { Content } from "../models/contents.entity";


@Injectable()
export class ContentsRepository extends AbstractRepository<Content> {
  constructor(
    @Inject(APP_CONFIG)
    private readonly appConfig: AppConfig,
    protected override readonly http: HttpClient
  ) {
    super(http, Content);
    this.apiUrl = this.appConfig.apiUrl;
  }

  getEntityName(): string {
    return "contents";
  }

  findAll(data: any): Observable<Content[]> {
    return this.http
      .post<any>([this.apiUrl, this.getEntityName(), 'all'].join("/"), data)
      .pipe(
        map(({ items, ...rest }) => ({
          ...rest,
          items: items.map((item: any) => this.instantiate(item)),
        }))
      );
  }

  talkerPaginate(filter: FilterInterface<any, 'createdAt' | 'activatedAt'>): Observable<FindResult<Content>> {

    return this.http.post<FindResult<Content>>([ this.apiUrl, this.getEntityName(), 'talker-paginate' ].join('/'), filter).pipe(
      map(({ items, ...rest }) => ({
        ...rest,
        items: items.map(item => this.instantiate(item))
      }))
    );
  }

  paginate(filter: FilterInterface<any, 'createdAt' | 'activatedAt'>): Observable<FindResult<Content>> {

    return this.http.post<FindResult<Content>>([ this.apiUrl, this.getEntityName(), 'paginate' ].join('/'), filter).pipe(
      map(({ items, ...rest }) => ({
        ...rest,
        items: items.map(item => this.instantiate(item))
      }))
    );
  }

  addFavorite(contentId: string): Observable<any> {
    return this.http.post<any>([this.apiUrl, this.getEntityName(), contentId, 'favorite'].join('/'), {});
  }

  removeFavorite(contentId: string): Observable<void> {
    return this.http.delete<void>([this.apiUrl, this.getEntityName(), contentId, 'favorite'].join('/'));
  }

  getFormationContents(filter: FilterInterface<any, 'createdAt' | 'activatedAt'>): Observable<FindResult<Content>> {
    return this.http.post<FindResult<Content>>([this.apiUrl, this.getEntityName(), 'formations-contents'].join('/'), filter).pipe(
      map(({ items, ...rest }) => ({
        ...rest,
        items: items.map(item => this.instantiate(item))
      }))
    );
  }

  /**
   * Search contents by title for admin autocomplete (pinned resources feature).
   * @param search - Search term to match against title
   * @param limit - Maximum number of results to return
   * @returns Array of contents with id and title
   */
  searchByTitle(search: string, limit: number = 10): Observable<Array<{ id: string; title: string }>> {
    return this.http.get<Array<{ id: string; title: string }>>(
      [this.apiUrl, this.getEntityName(), 'search'].join('/'),
      { params: { search, limit: limit.toString() } }
    );
  }

  /**
   * Get contents by IDs for admin (pinned resources feature).
   * @param ids - Array of content IDs
   * @returns Array of contents with id and title
   */
  getByIds(ids: string[]): Observable<Array<{ id: string; title: string }>> {
    return this.http.post<Array<{ id: string; title: string }>>(
      [this.apiUrl, this.getEntityName(), 'by-ids'].join('/'),
      { ids }
    );
  }
}